/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import DataTable from 'src/components-new/data/DataTable'
import {PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import AppDialog from 'src/components-new/feedback/AppDialog'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import useFilePreview from 'src/components-new/feedback/useFilePreview'
import Button from 'src/components-new/primitives/Button'
import {docTypesOptions, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {buildColumns, DocRecord} from 'src/pages-new/documents/columns'
import DocumentInfoDialogNew from 'src/pages-new/documents/DocumentInfoDialogNew'
import UploaderDialogNew from 'src/pages-new/documents/UploaderDialogNew'

// Mirror di DocumentsNew.tsx (src/pages-new/documents), ma sorgente
// myDocumentsConfigurations.personalDocuments (flag di upload `documentsUploadEnabled`
// invece di `uploadEnabled`, categoryOptions/filtersConfig già piatti anziché
// annidati in categoriesFilters/categoriesUpload).
export default function PersonalDocumentsTab(): JSX.Element {
  const personalDocumentsConfig = useAppSelector(
    (state) => state.pspConfigReducer.data.myDocumentsConfigurations?.personalDocuments
  )
  const userInfoData = useAppSelector(({userReducer}) => userReducer.data)

  const categoryOptions: {value: string; label: string}[] = personalDocumentsConfig?.categoryOptions ?? []
  // Categories offered when uploading: psp-backend leaves out the ones not meant for upload,
  // while table, filters and labels keep the full categoryOptions. Fallback for psp-backend
  // versions that do not send categoriesUpload yet.
  const uploadCategoryOptions: {value: string; label: string}[] = personalDocumentsConfig?.categoriesUpload ?? categoryOptions
  // "Nome file" is dropped as its own filter: the "Nome" column/search already covers it
  // (see apiClient.getDocuments), so showing both only confused users into typing the same
  // value into the wrong box when a document's `name` metadata was blank.
  const filtersConfig: FilterItem[] = (personalDocumentsConfig?.filtersConfig ?? [])
    .filter((item: FilterItem) => item.id !== 'fileName')
  const uploadEnabled = personalDocumentsConfig?.documentsUploadEnabled ?? false

  const [data, setData] = useState<DocRecord[]>([])
  const [count, setCount] = useState(0)
  const [loadStatus, setLoadStatus] = useState<string>(STATUS.PENDING)
  const [statusDelete, setStatusDelete] = useState<string | undefined>()
  const [statusPatch, setStatusPatch] = useState<string | undefined>()
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    rowsPerPage: 10,
    order: '-createdAt',
  })
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])

  const toast = useToast()
  const {viewerState, statusDownload, preview, close} = useFilePreview()

  // dialogs
  const [infoDoc, setInfoDoc] = useState<DocRecord | null>(null)
  const [deleteDoc, setDeleteDoc] = useState<DocRecord | null>(null)
  const [uploaderOpen, setUploaderOpen] = useState(false)

  // ── helpers ───────────────────────────────────────────────────────────────

  const parseCategoryToLabel = (categoryValue: string): string | undefined =>
    categoryOptions.find((c) => c.value === categoryValue)?.label

  const parseCategories = (category: any): string => {
    if (category && category.length > 0) {
      return category
        .map((categ: string) => parseCategoryToLabel(categ))
        .join(', ')
    }
    return '-'
  }

  const handleView = (row: DocRecord) => {
    if (!row.fileId) return
    const extension = row.fileName?.includes('.') ? (row.fileName.split('.').pop() ?? '') : ''
    preview({
      fileId: row.fileId,
      fileName: row.fileName ?? '',
      downloadName: `${row.user?.firstName}_${row.user?.lastName}_${parseCategoryToLabel(row.category?.[0] ?? '')}.${extension}`,
    })
  }

  useEffect(() => {
    if (statusPatch === STATUS.REJECTED) toast.error('Errore nel modificare il documento.')
  }, [statusPatch])

  useEffect(() => {
    if (statusDelete === STATUS.REJECTED) toast.error('Errore nel cancellare il documento.')
  }, [statusDelete])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) toast.error('Errore nel scaricare il documento.')
  }, [statusDownload])

  // ── data fetching ─────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoadStatus(STATUS.PENDING)
    const fetched: any[] = (await apiClient.getDocuments(activeFilters, setLoadStatus, pagination)) ?? []
    const parsed: DocRecord[] = fetched.map((doc: any) => ({
      ...doc,
      categoryReadable: parseCategories(doc.category),
      type: docTypesOptions.find((t: any) => t.value === doc.type)?.label,
    }))
    setData(parsed)
    const cnt = (await apiClient.getDocumentsCount(activeFilters, () => {})) ?? 0
    setCount(Number(cnt))
  }, [activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination((p) => ({...p, page: 0}))
  }

  const handleEdit = async (
    id: string,
    payload: {category: string[]; name: string; description: string}
  ) => {
    await apiClient.patchDocuments(id, payload, setStatusPatch)
    setInfoDoc(null)
    fetchData()
  }

  const handleDelete = async () => {
    if (!deleteDoc) return
    await apiClient.deleteDocument(deleteDoc._id, setStatusDelete)
    setDeleteDoc(null)
    fetchData()
  }

  const handleUploaded = () => {
    setUploaderOpen(false)
    fetchData()
  }

  // ── derived ───────────────────────────────────────────────────────────────

  const columns = buildColumns({
    onInfo: (row) => setInfoDoc(row),
    onView: handleView,
    onDelete: (row) => setDeleteDoc(row),
    authUserId: userInfoData?.authUserId,
    columnOptions: personalDocumentsConfig?.columnOptions,
  })
  // Only show the mobile card header when "name" survived the PSP's columnOptions
  // (or no config was set at all) — otherwise a disabled column would still show up there.
  const hasNameColumn = columns.some((col) => col.key === 'name')

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2}}>
        {uploadEnabled && (
          <Button variant="primary" startIcon={<FileUploadOutlinedIcon fontSize="small" />} onClick={() => setUploaderOpen(true)}>
            Carica documento
          </Button>
        )}
        <Filters config={filtersConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      {loadStatus === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setLoadStatus(STATUS.FULFILLED)} sx={{mb: 2}}>
          {'Errore nel recuperare i documenti.'}
        </InlineMessage>
      )}

      <DataTable<DocRecord>
        columns={columns}
        rows={data}
        keyField="_id"
        loading={loadStatus === STATUS.PENDING}
        emptyMessage="Nessun documento trovato"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        onRowClick={handleView}
        mobileCardTitle={hasNameColumn ? (row) => row.name ?? row.fileName ?? '—' : undefined}
      />

      <UploaderDialogNew
        open={uploaderOpen}
        onClose={() => setUploaderOpen(false)}
        onUploaded={handleUploaded}
        categoryOptions={uploadCategoryOptions}
      />

      {infoDoc && (
        <DocumentInfoDialogNew
          doc={infoDoc}
          open={!!infoDoc}
          onClose={() => setInfoDoc(null)}
          onSave={handleEdit}
          readOnly={infoDoc.creatorId !== userInfoData?.authUserId}
          categoryOptions={categoryOptions}
        />
      )}

      <AppDialog
        open={!!deleteDoc}
        onClose={() => setDeleteDoc(null)}
        title="Conferma eliminazione documento"
        actions={
          <>
            <Button variant="secondary" onClick={() => setDeleteDoc(null)}>
              Annulla
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Ok
            </Button>
          </>
        }
      >
        <Typography sx={{pt: 1}}>Vuoi eliminare questo documento?</Typography>
      </AppDialog>

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={viewerState.mime}
        onClose={close}
        open={viewerState.open}
        status={viewerState.status}
      />
    </Box>
  )
}
